import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  UserPlus, 
  Edit, 
  Trash2, 
  Shield, 
  Mail,
  Calendar,
  CheckCircle,
  XCircle,
  Users,
  Key
} from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Grouped permissions from backend
const PERMISSION_GROUPS = {
  core_management: {
    label: 'Core Management',
    permissions: [
      { key: 'view_dashboard', label: 'View Dashboard' },
      { key: 'manage_users', label: 'Manage Users' },
      { key: 'manage_roles', label: 'Manage Roles' },
    ]
  },
  lead_client_management: {
    label: 'Lead & Client Management',
    permissions: [
      { key: 'manage_leads', label: 'Manage Leads' },
      { key: 'manage_clients', label: 'Manage Clients' },
      { key: 'manage_activities', label: 'Manage Activities' },
      { key: 'manage_consultations', label: 'Manage Consultations' },
    ]
  },
  smart_lead_tools: {
    label: 'Smart Lead Tools',
    permissions: [
      { key: 'manage_lead_scoring', label: 'Manage Lead Scoring' },
      { key: 'manage_lead_assignment', label: 'Manage Lead Assignment' },
      { key: 'manage_workflows', label: 'Manage Workflows' },
      { key: 'manage_lead_pull', label: 'Manage Lead Pull' },
      { key: 'use_ai_followup', label: 'Use AI Follow-Up' },
    ]
  },
  content_settings: {
    label: 'Content & Settings',
    permissions: [
      { key: 'manage_services', label: 'Manage Services' },
      { key: 'manage_country_services', label: 'Manage Country Services' },
      { key: 'manage_testimonials', label: 'Manage Testimonials' },
      { key: 'manage_blogs', label: 'Manage Blogs' },
      { key: 'manage_announcements', label: 'Manage Announcements' },
      { key: 'manage_content', label: 'Manage Page Content' },
      { key: 'manage_callbacks', label: 'Manage Callback Slots' },
      { key: 'manage_settings', label: 'Manage Settings' },
      { key: 'manage_chatbot', label: 'Manage Chatbot' },
      { key: 'manage_one92_settings', label: 'Manage One92 Settings' },
      { key: 'manage_newsletter', label: 'Manage Newsletter' },
      { key: 'manage_privacy_policy', label: 'Manage Privacy Policy' },
    ]
  },
  analytics_integrations: {
    label: 'Analytics & Integrations',
    permissions: [
      { key: 'view_analytics', label: 'View Analytics' },
      { key: 'export_data', label: 'Export Data' },
      { key: 'manage_integrations', label: 'Manage Integrations' },
      { key: 'manage_api_keys', label: 'Manage API Keys' },
      { key: 'manage_data', label: 'Manage Data (Clear/Import/Export)' },
    ]
  },
  support_help: {
    label: 'Support & Help',
    permissions: [
      { key: 'view_help_center', label: 'View Help Center' },
      { key: 'manage_live_chats', label: 'Manage Live Chats' },
      { key: 'view_client_portal', label: 'View Client Portal' },
    ]
  },
  billing_management: {
    label: 'Billing & Financial Management',
    permissions: [
      { key: 'view_proposals', label: 'View Proposals' },
      { key: 'create_proposals', label: 'Create Proposals' },
      { key: 'edit_proposals', label: 'Edit Proposals' },
      { key: 'delete_proposals', label: 'Delete Proposals' },
      { key: 'approve_proposals', label: 'Approve/Reject Proposals' },
      { key: 'download_proposals', label: 'Download Proposal PDFs' },
      { key: 'view_agreements', label: 'View Agreements' },
      { key: 'create_agreements', label: 'Create Agreements' },
      { key: 'edit_agreements', label: 'Edit Agreements' },
      { key: 'delete_agreements', label: 'Delete Agreements' },
      { key: 'approve_agreements', label: 'Approve/Sign Agreements' },
      { key: 'download_agreements', label: 'Download Agreement PDFs' },
      { key: 'view_invoices', label: 'View Invoices' },
      { key: 'create_invoices', label: 'Create Invoices' },
      { key: 'edit_invoices', label: 'Edit Invoices' },
      { key: 'delete_invoices', label: 'Delete Invoices' },
      { key: 'download_invoices', label: 'Download Invoice PDFs' },
      { key: 'send_invoices', label: 'Send Invoice Emails' },
      { key: 'view_payments', label: 'View Payment Records' },
      { key: 'record_payments', label: 'Record Payments' },
      { key: 'edit_payments', label: 'Edit Payment Records' },
      { key: 'delete_payments', label: 'Delete Payment Records' },
      { key: 'download_payment_receipts', label: 'Download Payment Receipts' },
      { key: 'upload_payment_screenshots', label: 'Upload Payment Screenshots' },
    ]
  }
};

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  sales: 'Sales',
  process_consultant: 'Process Consultant'
};

const ROLE_COLORS = {
  super_admin: 'bg-purple-100 text-purple-800',
  admin: 'bg-blue-100 text-blue-800',
  sales: 'bg-green-100 text-green-800',
  process_consultant: 'bg-orange-100 text-orange-800'
};

const UserManagement = () => {
  const { getAuthHeader, admin: currentAdmin } = useAuth();
  const { toast } = useToast();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]); // Dynamic roles from API
  const [loading, setLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isPasswordResetDialogOpen, setIsPasswordResetDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    consultant_name: '',
    contact_number: '',
    photo: '',
    gender: '',
    bio: '',
    priority: 1,
    role: 'admin',
    roles: ['admin'],  // NEW: Array for dual/multi-role support (initialize with primary role)
    permissions: []
  });
  
  // Photo upload state
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchRoles(); // Fetch roles on mount
  }, []);

  const fetchRoles = async () => {
    try {
      console.log('Fetching roles from:', `${API}/admin/roles`);
      const response = await axios.get(`${API}/admin/roles`, {
        headers: getAuthHeader()
      });
      console.log('Fetched roles:', response.data);
      console.log('Roles count:', response.data.length);
      console.log('Roles details:', JSON.stringify(response.data, null, 2));
      setRoles(response.data);
    } catch (error) {
      console.error('Error fetching roles:', error);
      console.error('Error details:', error.response?.data);
      console.error('Error status:', error.response?.status);
      toast({
        title: 'Error',
        description: 'Failed to load roles',
        variant: 'destructive'
      });
    }
  };

  const getRoleLabel = (roleKey) => {
    const role = roles.find(r => r.role_key === roleKey);
    return role ? role.role_name : ROLE_LABELS[roleKey] || roleKey;
  };

  const getRoleColor = (roleKey) => {
    return ROLE_COLORS[roleKey] || 'bg-gray-100 text-gray-800';
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: getAuthHeader()
      });
      setUsers(response.data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load users',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    try {
      const response = await axios.post(`${API}/users`, formData, {
        headers: getAuthHeader()
      });
      
      const newUserId = response.data.id;
      
      // Upload photo if selected
      if (photoFile) {
        await uploadPhoto(newUserId);
      }
      
      toast({
        title: 'Success',
        description: photoFile ? 'User created and photo uploaded successfully' : 'User created successfully'
      });
      
      setIsCreateDialogOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to create user',
        variant: 'destructive'
      });
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    console.log('Update user clicked', formData);
    
    try {
      const updateData = {
        username: formData.username,  // Include username for updating user ID
        email: formData.email,
        consultant_name: formData.consultant_name,
        contact_number: formData.contact_number,
        photo: formData.photo,
        gender: formData.gender,
        bio: formData.bio,
        priority: formData.priority,
        role: formData.role,
        roles: formData.roles,  // Include roles array for dual role support
        permissions: formData.permissions,
        is_active: formData.is_active
      };
      
      console.log('Updating user:', editingUser.id, updateData);
      
      const response = await axios.put(`${API}/users/${editingUser.id}`, updateData, {
        headers: getAuthHeader()
      });
      
      console.log('Update response:', response.data);
      
      // Upload photo if selected
      if (photoFile) {
        await uploadPhoto(editingUser.id);
      }
      
      toast({
        title: 'Success',
        description: photoFile ? 'User updated and photo uploaded successfully' : 'User updated successfully'
      });
      
      alert(photoFile ? 'User updated and photo uploaded successfully!' : 'User updated successfully!');
      
      setIsEditDialogOpen(false);
      setEditingUser(null);
      resetForm();
      fetchUsers();
    } catch (error) {
      console.error('Update error:', error);
      console.error('Error response:', error.response);
      console.error('Error data:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to update user';
      
      toast({
        title: 'Error',
        description: errorMsg,
        variant: 'destructive'
      });
      
      alert('Error: ' + errorMsg + '\nStatus: ' + (error.response?.status || 'Unknown') + '\nCheck console for details');
    }
  };

  const handleDeleteUser = async (userId) => {
    const confirmed = await window.confirm('Are you sure you want to delete this user?');
    if (!confirmed) {
      return;
    }
    
    try {
      await axios.delete(`${API}/users/${userId}`, {
        headers: getAuthHeader()
      });
      
      toast({
        title: 'Success',
        description: 'User deleted successfully'
      });
      
      fetchUsers();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to delete user',
        variant: 'destructive'
      });
    }
  };

  const handleToggleActiveStatus = async (user) => {
    try {
      await axios.put(`${API}/users/${user.id}`, {
        is_active: !user.is_active
      }, {
        headers: getAuthHeader()
      });
      
      toast({
        title: 'Success',
        description: `User ${!user.is_active ? 'activated' : 'deactivated'} successfully`
      });
      
      fetchUsers();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update user status',
        variant: 'destructive'
      });
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    try {
      await axios.put(`${API}/users/${resetPasswordUser.id}`, {
        password: newPassword
      }, {
        headers: getAuthHeader()
      });
      
      toast({
        title: 'Success',
        description: 'Password reset successfully'
      });
      
      setIsPasswordResetDialogOpen(false);
      setResetPasswordUser(null);
      setNewPassword('');
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to reset password',
        variant: 'destructive'
      });
    }
  };

  const openEditDialog = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      email: user.email,
      password: '',
      consultant_name: user.consultant_name || '',
      contact_number: user.contact_number || '',
      photo: user.photo || '',
      gender: user.gender || '',
      bio: user.bio || '',
      priority: user.priority || 1,
      role: user.role,
      roles: user.roles || [user.role],  // Load roles array or fallback to primary role
      permissions: user.permissions,
      is_active: user.is_active
    });
    setPhotoFile(null);
    setPhotoPreview(null);
    setIsEditDialogOpen(true);
  };

  const openPasswordResetDialog = (user) => {
    setResetPasswordUser(user);
    setNewPassword('');
    setIsPasswordResetDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      username: '',
      email: '',
      password: '',
      consultant_name: '',
      contact_number: '',
      photo: '',
      gender: '',
      bio: '',
      priority: 1,
      role: 'admin',
      roles: ['admin'],  // Initialize with primary role
      permissions: []
    });
    setPhotoFile(null);
    setPhotoPreview(null);
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast({
          title: 'Error',
          description: 'Please select an image file',
          variant: 'destructive'
        });
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: 'Error',
          description: 'Image size must be less than 5MB',
          variant: 'destructive'
        });
        return;
      }
      
      setPhotoFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadPhoto = async (userId) => {
    if (!photoFile) return null;
    
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('photo', photoFile);
      
      const response = await axios.post(
        `${API}/users/${userId}/upload-photo`,
        formData,
        {
          headers: {
            ...getAuthHeader(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      return response.data.photo_path;
    } catch (error) {
      console.error('Photo upload error:', error);
      toast({
        title: 'Warning',
        description: 'User created but photo upload failed',
        variant: 'destructive'
      });
      return null;
    } finally {
      setUploadingPhoto(false);
    }
  };

  const togglePermission = (permKey) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permKey)
        ? prev.permissions.filter(p => p !== permKey)
        : [...prev.permissions, permKey]
    }));
  };

  const toggleGroupPermissions = (groupKey) => {
    const group = PERMISSION_GROUPS[groupKey];
    const groupPermKeys = group.permissions.map(p => p.key);
    const allSelected = groupPermKeys.every(key => formData.permissions.includes(key));
    
    if (allSelected) {
      // Deselect all
      setFormData(prev => ({
        ...prev,
        permissions: prev.permissions.filter(p => !groupPermKeys.includes(p))
      }));
    } else {
      // Select all
      setFormData(prev => ({
        ...prev,
        permissions: [...new Set([...prev.permissions, ...groupPermKeys])]
      }));
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner type="circular" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2" style={{ color: '#A08EA8' }}>
            <Users className="w-8 h-8" />
            User Management
          </h1>
          <p className="text-gray-600 mt-1">Manage admin users, roles, and permissions</p>
        </div>
        
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => resetForm()} style={{ background: 'linear-gradient(135deg, #A08EA8 0%, #7D6882 100%)', color: 'white' }}>
              <UserPlus className="w-4 h-4 mr-2" />
              Add User
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create New User</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div>
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="consultant_name">Consultant Name</Label>
                <Input
                  id="consultant_name"
                  value={formData.consultant_name}
                  onChange={(e) => setFormData({ ...formData, consultant_name: e.target.value })}
                  placeholder="Display name"
                />
              </div>

              <div>
                <Label htmlFor="contact_number">Contact Number</Label>
                <Input
                  id="contact_number"
                  value={formData.contact_number}
                  onChange={(e) => setFormData({ ...formData, contact_number: e.target.value })}
                  placeholder="+91-9876543210"
                />
              </div>

              <div>
                <Label htmlFor="gender">Gender</Label>
                <select
                  id="gender"
                  value={formData.gender}
                  onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">Select gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
                <small style={{fontSize: '0.75rem', color: '#6b7280'}}>Used for placeholder avatar if no photo uploaded</small>
              </div>

              <div>
                <Label htmlFor="bio">Consultant Bio</Label>
                <textarea
                  id="bio"
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  placeholder="Brief introduction about the consultant that will be shown in welcome emails to clients..."
                  rows="3"
                  className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  style={{resize: 'vertical'}}
                />
                <small style={{fontSize: '0.75rem', color: '#6b7280'}}>This bio will appear in welcome emails to help clients know their consultant better</small>
              </div>

              <div>
                <Label htmlFor="photo">Profile Photo</Label>
                <div className="space-y-2">
                  <input
                    id="photo"
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm file:border-0 file:bg-transparent file:text-sm file:font-medium"
                  />
                  {photoPreview && (
                    <div className="flex items-center gap-3">
                      <img 
                        src={photoPreview} 
                        alt="Preview" 
                        className="w-20 h-20 rounded-full object-cover border-2 border-purple-500"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setPhotoFile(null);
                          setPhotoPreview(null);
                        }}
                        className="text-sm text-red-600 hover:text-red-800"
                      >
                        Remove
                      </button>
                    </div>
                  )}
                  <small style={{fontSize: '0.75rem', color: '#6b7280'}}>Upload consultant photo (max 5MB, JPG/PNG/GIF/WEBP)</small>
                </div>
              </div>

              <div>
                <Label htmlFor="priority">Priority (1-10)</Label>
                <Input
                  id="priority"
                  type="number"
                  min="1"
                  max="10"
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 1 })}
                />
                <small style={{fontSize: '0.75rem', color: '#6b7280'}}>Lower number = higher priority for assignment</small>
              </div>
              
              <div>
                <Label htmlFor="role">Primary Role *</Label>
                <select
                  id="role"
                  value={formData.role}
                  onChange={(e) => {
                    const newRole = e.target.value;
                    const currentRoles = formData.roles || [];
                    setFormData({ 
                      ...formData, 
                      role: newRole,
                      // Ensure primary role is in roles array
                      roles: currentRoles.includes(newRole) ? currentRoles : [...currentRoles, newRole]
                    });
                  }}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  required
                >
                  <option value="">
                    {roles.length === 0 ? "Loading roles..." : "Select primary role"}
                  </option>
                  {roles.map(role => (
                    <option key={role.id} value={role.role_key}>
                      {role.role_name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <Label>Additional Roles (Dual Role Support)</Label>
                <div className="border rounded p-3 mt-2 bg-gray-50">
                  <p className="text-xs text-gray-500 mb-3">Select additional roles for this user (e.g., Sales + Process Consultant)</p>
                  <div className="grid grid-cols-2 gap-2">
                    {roles.map(role => {
                      const currentRoles = formData.roles || [];
                      const isChecked = currentRoles.includes(role.role_key);
                      const isPrimary = formData.role === role.role_key;
                      
                      return (
                        <label 
                          key={role.id} 
                          className={`flex items-center space-x-2 cursor-pointer p-2 rounded transition-colors ${
                            isChecked ? 'bg-purple-50 border border-purple-200' : 'bg-white border border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            disabled={isPrimary}
                            onChange={(e) => {
                              const currentRoles = formData.roles || [];
                              if (e.target.checked) {
                                setFormData({ ...formData, roles: [...currentRoles, role.role_key] });
                              } else {
                                setFormData({ ...formData, roles: currentRoles.filter(r => r !== role.role_key) });
                              }
                            }}
                            className="rounded"
                            style={{ accentColor: '#A08EA8' }}
                          />
                          <span className={`text-sm ${isChecked ? 'font-semibold text-purple-800' : 'text-gray-700'}`}>
                            {role.role_name}
                            {isPrimary && <span className="ml-2 text-xs text-purple-600">(Primary)</span>}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>
              
              <div>
                <Label>Permissions</Label>
                <div className="border rounded p-4 max-h-96 overflow-y-auto mt-2">
                  {Object.entries(PERMISSION_GROUPS).map(([groupKey, group]) => (
                    <div key={groupKey} className="mb-4 pb-4 border-b last:border-b-0">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-semibold text-sm" style={{ color: '#A08EA8' }}>{group.label}</h3>
                        <button
                          type="button"
                          onClick={() => toggleGroupPermissions(groupKey)}
                          className="text-xs px-2 py-1 rounded"
                          style={{ 
                            background: group.permissions.every(p => formData.permissions.includes(p.key)) 
                              ? 'linear-gradient(135deg, #A08EA8 0%, #7D6882 100%)' 
                              : '#f3f4f6',
                            color: group.permissions.every(p => formData.permissions.includes(p.key)) ? 'white' : '#6b7280'
                          }}
                        >
                          {group.permissions.every(p => formData.permissions.includes(p.key)) ? 'Deselect All' : 'Select All'}
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {group.permissions.map(perm => (
                          <label key={perm.key} className="flex items-center space-x-2 cursor-pointer p-2 rounded hover:bg-gray-50">
                            <input
                              type="checkbox"
                              checked={formData.permissions.includes(perm.key)}
                              onChange={() => togglePermission(perm.key)}
                              className="rounded"
                              style={{ accentColor: '#A08EA8' }}
                            />
                            <span className="text-sm text-gray-700">{perm.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" style={{ background: 'linear-gradient(135deg, #A08EA8 0%, #7D6882 100%)', color: 'white' }}>
                  Create User
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead style={{ background: 'linear-gradient(135deg, #A08EA8 0%, #7D6882 100%)' }}>
            <tr>
              <th className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
                Email
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
                Role
              </th>
              <th className="px-6 py-3 text-center text-xs font-semibold text-white uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-right text-xs font-semibold text-white uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users.map(user => (
              <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10">
                      <div className="h-10 w-10 rounded-full flex items-center justify-center text-white font-semibold" style={{ background: 'linear-gradient(135deg, #A08EA8 0%, #7D6882 100%)' }}>
                        {user.username.charAt(0).toUpperCase()}
                      </div>
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">{user.username}</div>
                      <div className="text-xs text-gray-500">
                        Joined {new Date(user.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center text-sm text-gray-900">
                    <Mail className="w-4 h-4 mr-2 text-gray-400" />
                    {user.email}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex flex-wrap gap-1">
                    {/* Show all roles (primary + additional) */}
                    {(user.roles && user.roles.length > 0 ? user.roles : [user.role]).map((role, index) => (
                      <span 
                        key={index} 
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getRoleColor(role)}`}
                      >
                        {index === 0 && <Shield className="w-3 h-3 mr-1" />}
                        {getRoleLabel(role)}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <label className="flex items-center justify-center cursor-pointer">
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={user.is_active}
                        onChange={() => handleToggleActiveStatus(user)}
                        disabled={user.id === currentAdmin?.id}
                        className="sr-only"
                      />
                      <div className={`block w-14 h-8 rounded-full ${
                        user.is_active ? 'bg-green-500' : 'bg-gray-300'
                      }`}></div>
                      <div className={`dot absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition ${
                        user.is_active ? 'transform translate-x-6' : ''
                      }`}></div>
                    </div>
                    <span className={`ml-3 text-sm font-medium ${
                      user.is_active ? 'text-green-600' : 'text-gray-500'
                    }`}>
                      {user.is_active ? <CheckCircle className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
                    </span>
                  </label>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => openEditDialog(user)}
                      className="p-2 rounded-lg transition-colors"
                      style={{ background: '#f3f4f6', color: '#A08EA8' }}
                      title="Edit"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => openPasswordResetDialog(user)}
                      className="p-2 rounded-lg transition-colors"
                      style={{ background: '#f3f4f6', color: '#A08EA8' }}
                      title="Reset Password"
                    >
                      <Key className="w-4 h-4" />
                    </button>
                    {user.id !== currentAdmin?.id && (
                      <button
                        onClick={() => handleDeleteUser(user.id)}
                        className="text-red-600 hover:text-red-900 p-2 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit User: {editingUser?.username}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateUser} className="space-y-4">
            <div>
              <Label htmlFor="edit-username">Username (User ID)</Label>
              <Input
                id="edit-username"
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                required
              />
              <small style={{fontSize: '0.75rem', color: '#6b7280'}}>This is the login username/ID for the user</small>
            </div>

            <div>
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </div>

            <div>
              <Label htmlFor="edit-consultant-name">Consultant Name (Display Name)</Label>
              <Input
                id="edit-consultant-name"
                type="text"
                value={formData.consultant_name}
                onChange={(e) => setFormData({ ...formData, consultant_name: e.target.value })}
                placeholder="Name shown to clients"
              />
            </div>

            <div>
              <Label htmlFor="edit-contact-number">Contact Number</Label>
              <Input
                id="edit-contact-number"
                type="text"
                value={formData.contact_number}
                onChange={(e) => setFormData({ ...formData, contact_number: e.target.value })}
                placeholder="+91-9876543210"
              />
            </div>

            <div>
              <Label htmlFor="edit-gender">Gender</Label>
              <select
                id="edit-gender"
                value={formData.gender}
                onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="">Select gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
              <small style={{fontSize: '0.75rem', color: '#6b7280'}}>Used for placeholder avatar if no photo uploaded</small>
            </div>

            <div>
              <Label htmlFor="edit-bio">Consultant Bio</Label>
              <textarea
                id="edit-bio"
                value={formData.bio}
                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                placeholder="Brief introduction about the consultant that will be shown in welcome emails to clients..."
                rows="3"
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                style={{resize: 'vertical'}}
              />
              <small style={{fontSize: '0.75rem', color: '#6b7280'}}>This bio will appear in welcome emails to help clients know their consultant better</small>
            </div>

            <div>
              <Label htmlFor="edit-photo">Profile Photo</Label>
              <div className="space-y-2">
                {editingUser?.photo && !photoPreview && (
                  <div className="flex items-center gap-3 mb-2">
                    <img 
                      src={`${API}/users/${editingUser.id}/photo`} 
                      alt="Current" 
                      className="w-20 h-20 rounded-full object-cover border-2 border-gray-300"
                    />
                    <span className="text-sm text-gray-600">Current Photo</span>
                  </div>
                )}
                <input
                  id="edit-photo"
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm file:border-0 file:bg-transparent file:text-sm file:font-medium"
                />
                {photoPreview && (
                  <div className="flex items-center gap-3">
                    <img 
                      src={photoPreview} 
                      alt="Preview" 
                      className="w-20 h-20 rounded-full object-cover border-2 border-purple-500"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setPhotoFile(null);
                        setPhotoPreview(null);
                      }}
                      className="text-sm text-red-600 hover:text-red-800"
                    >
                      Remove New Photo
                    </button>
                  </div>
                )}
                <small style={{fontSize: '0.75rem', color: '#6b7280'}}>Upload new photo (max 5MB, JPG/PNG/GIF/WEBP)</small>
              </div>
            </div>

            <div>
              <Label htmlFor="edit-priority">Priority (Lower = Higher Priority)</Label>
              <Input
                id="edit-priority"
                type="number"
                min="1"
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
              />
              <p className="text-xs text-gray-500 mt-1">Used for round-robin lead assignment</p>
            </div>
            
            <div>
              <Label htmlFor="edit-role">Primary Role *</Label>
              <select
                id="edit-role"
                value={formData.role}
                onChange={(e) => {
                  const newRole = e.target.value;
                  const currentRoles = formData.roles || [];
                  setFormData({ 
                    ...formData, 
                    role: newRole,
                    // Ensure primary role is in roles array
                    roles: currentRoles.includes(newRole) ? currentRoles : [...currentRoles, newRole]
                  });
                }}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                required
                disabled={currentAdmin?.id === editingUser?.id}
              >
                <option value="">
                  {roles.length === 0 ? "Loading roles..." : "Select primary role"}
                </option>
                {roles.length === 0 ? null : currentAdmin?.id === editingUser?.id ? (
                  // If editing self, show current role only
                  roles.filter(role => role.role_key === editingUser.role).map(role => (
                    <option key={role.id} value={role.role_key}>
                      {role.role_name}
                    </option>
                  ))
                ) : (
                  // Show all roles for other users
                  roles.map(role => (
                    <option key={role.id} value={role.role_key}>
                      {role.role_name}
                    </option>
                  ))
                )}
              </select>
              {currentAdmin?.id === editingUser?.id && (
                <p className="text-xs text-gray-500 mt-1">You cannot change your own primary role</p>
              )}
            </div>

            <div>
              <Label>Additional Roles (Dual Role Support)</Label>
              <div className="border rounded p-3 mt-2 bg-gray-50">
                <p className="text-xs text-gray-500 mb-3">Select additional roles for this user (e.g., Sales + Process Consultant)</p>
                <div className="grid grid-cols-2 gap-2">
                  {roles.map(role => {
                    const currentRoles = formData.roles || [];
                    const isChecked = currentRoles.includes(role.role_key);
                    const isPrimary = formData.role === role.role_key;
                    const isSelfEdit = currentAdmin?.id === editingUser?.id;
                    
                    return (
                      <label 
                        key={role.id} 
                        className={`flex items-center space-x-2 cursor-pointer p-2 rounded transition-colors ${
                          isChecked ? 'bg-purple-50 border border-purple-200' : 'bg-white border border-gray-200 hover:bg-gray-50'
                        } ${(isPrimary || isSelfEdit) ? 'opacity-50' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          disabled={isPrimary || isSelfEdit}
                          onChange={(e) => {
                            const currentRoles = formData.roles || [];
                            if (e.target.checked) {
                              setFormData({ ...formData, roles: [...currentRoles, role.role_key] });
                            } else {
                              setFormData({ ...formData, roles: currentRoles.filter(r => r !== role.role_key) });
                            }
                          }}
                          className="rounded"
                          style={{ accentColor: '#A08EA8' }}
                        />
                        <span className={`text-sm ${isChecked ? 'font-semibold text-purple-800' : 'text-gray-700'}`}>
                          {role.role_name}
                          {isPrimary && <span className="ml-2 text-xs text-purple-600">(Primary)</span>}
                        </span>
                      </label>
                    );
                  })}
                </div>
                {currentAdmin?.id === editingUser?.id && (
                  <p className="text-xs text-amber-600 mt-2">⚠️ You cannot modify your own roles</p>
                )}
              </div>
            </div>
            
            <div>
              <Label>Permissions</Label>
              <div className="border rounded p-4 max-h-96 overflow-y-auto mt-2">
                {Object.entries(PERMISSION_GROUPS).map(([groupKey, group]) => (
                  <div key={groupKey} className="mb-4 pb-4 border-b last:border-b-0">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold text-sm" style={{ color: '#A08EA8' }}>{group.label}</h3>
                      <button
                        type="button"
                        onClick={() => toggleGroupPermissions(groupKey)}
                        className="text-xs px-2 py-1 rounded"
                        style={{ 
                          background: group.permissions.every(p => formData.permissions.includes(p.key)) 
                            ? 'linear-gradient(135deg, #A08EA8 0%, #7D6882 100%)' 
                            : '#f3f4f6',
                          color: group.permissions.every(p => formData.permissions.includes(p.key)) ? 'white' : '#6b7280'
                        }}
                      >
                        {group.permissions.every(p => formData.permissions.includes(p.key)) ? 'Deselect All' : 'Select All'}
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {group.permissions.map(perm => (
                        <label key={perm.key} className="flex items-center space-x-2 cursor-pointer p-2 rounded hover:bg-gray-50">
                          <input
                            type="checkbox"
                            checked={formData.permissions.includes(perm.key)}
                            onChange={() => togglePermission(perm.key)}
                            className="rounded"
                            style={{ accentColor: '#A08EA8' }}
                          />
                          <span className="text-sm text-gray-700">{perm.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" style={{ background: 'linear-gradient(135deg, #A08EA8 0%, #7D6882 100%)', color: 'white' }}>
                Update User
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Password Reset Dialog */}
      <Dialog open={isPasswordResetDialogOpen} onOpenChange={setIsPasswordResetDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password: {resetPasswordUser?.username}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div>
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsPasswordResetDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" style={{ background: 'linear-gradient(135deg, #A08EA8 0%, #7D6882 100%)', color: 'white' }}>
                Reset Password
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagement;